import type { LoadedPlugin, PluginManifest } from './types';
import { satisfies as semverSatisfies } from 'semver';
import { createPluginSdk } from './sdk';
import { fetchWithSWR } from '../utils/fetchWithSWR';
import { isPublicRoute, isManifestPublicPath } from '../core/auth/publicRoutes';
import {
	Class2PluginBroker,
	isClass2IsolationEnabled,
	class2SandboxFrameUrl,
} from '../core/plugins/Class2PluginBroker';

/**
 * PluginRegistry - Manages plugin discovery, loading, and SDK creation
 * 
 * Responsibilities:
 * - Fetch plugin manifests from BFF
 * - Load ESM bundles with version compatibility checks
 * - Create scoped SDKs with manifest-based authorization config
 * - Cache loaded plugins and bundles
 * - Inject CSS stylesheets from plugin manifests
 */
export class PluginRegistry {
	private loaded = new Map<string, LoadedPlugin>();
	private bundleCache = new Map<string, Promise<any>>();
	private cssCache = new Set<string>();  // Track loaded CSS to prevent duplicates
	private base = (import.meta as any).env?.VITE_BFF_BASE_URL || '';
	private queryClient: any;

	constructor(queryClient?: any) {
		this.queryClient = queryClient;
	}

	/**
	 * Discover plugin manifests (lightweight index for dashboard)
	 * Always uses lazy loading endpoint for fast initial load
	 */
	async discover(): Promise<PluginManifest[]> {
		const url = `${this.base}/api/plugins/manifests`;
		const { data } = await fetchWithSWR<any>(url, { method: 'GET' }, 10_000);
		// BFF returns array directly, not wrapped in { manifests: [] }
		return Array.isArray(data) ? data : (data.manifests || []);
	}

	/**
	 * Get full manifest for a specific plugin by ID
	 * Loads from individual manifest file (lazy loading)
	 */
	async getManifestById(pluginId: string): Promise<PluginManifest | null> {
		try {
			const url = `${this.base}/api/plugins/manifests/${encodeURIComponent(pluginId)}`;
			const { data } = await fetchWithSWR<PluginManifest>(url, { method: 'GET' }, 10_000);
			return data;
		} catch (error) {
			if ((error as any)?.status === 404) {
				return null;
			}
			console.error(`[Plugin] Failed to load manifest for ${pluginId}:`, error);
			return null;
		}
	}

	private importBundle(url: string): Promise<any> {
		if (!this.bundleCache.has(url)) {
			const promise = this.importWithAuthCheck(url).catch(err => {
				this.bundleCache.delete(url);
				throw err;
			});
			this.bundleCache.set(url, promise);
		}
		return this.bundleCache.get(url)!;
	}

	/**
	 * Native import() on a 401 URL throws an opaque TypeError with no
	 * access to the HTTP status. On failure we probe with fetch() to
	 * detect 401s and redirect to login instead of showing a broken UI.
	 */
	private async importWithAuthCheck(url: string): Promise<any> {
		try {
			return await import(/* @vite-ignore */ url);
		} catch (importErr) {
			try {
				const probe = await fetch(url, { method: 'HEAD', credentials: 'include' });
				if (probe.status === 401) {
					if (isPublicRoute()) {
						throw new Error('Authentication required');
					}
					const interceptor = (globalThis as any).__authInterceptor;
					if (interceptor?.handleUnauthorized) {
						await interceptor.handleUnauthorized(probe);
					} else {
						const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
						window.location.href = `/auth/login?return_to=${returnTo}`;
					}
					throw new Error('Authentication required');
				}
			} catch (probeErr) {
				if ((probeErr as Error).message === 'Authentication required') throw probeErr;
			}
			throw importErr;
		}
	}

	/**
	 * Load CSS files for a plugin
	 * Injects <link> tags into document head
	 * @param pluginId - Plugin identifier
	 * @param cssFiles - Array of CSS file paths from manifest.bundle.css
	 */
	private async loadCSS(pluginId: string, cssFiles: string[]): Promise<void> {
		if (!cssFiles || cssFiles.length === 0) return;

		const promises = cssFiles.map(cssFile => {
			// Extract filename from full path if needed
			// Handle both full paths like "/app/plugins/risk-analysis/1.0.0/styles.css" 
			// and relative paths like "styles.css" or "plugins/risk-analysis/1.0.0/styles.css"
			let cssFileName = cssFile;
			if (cssFile.includes('/')) {
				// Extract just the filename (last part after last slash)
				cssFileName = cssFile.split('/').pop() || cssFile;
			}
			
			// Create unique cache key for this CSS file
			const cacheKey = `${pluginId}:${cssFileName}`;
			
			// Skip if already loaded
			if (this.cssCache.has(cacheKey)) {
				console.log(`[Plugin] ${pluginId}: CSS already loaded: ${cssFileName}`);
				return Promise.resolve();
			}

			return new Promise<void>((resolve, reject) => {
				// Construct CSS URL using plugin-center endpoint
				// Plugin-center serves files from /plugins/files/<pluginId>/<filename>
				const cssUrl = `${this.base}/api/plugins/files/${encodeURIComponent(pluginId)}/${encodeURIComponent(cssFileName)}`;
				
				// Check if link already exists in DOM (belt and suspenders)
				const existing = document.querySelector(`link[href="${cssUrl}"]`);
				if (existing) {
					this.cssCache.add(cacheKey);
					resolve();
					return;
				}

				// Create and inject link tag
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = cssUrl;
				link.dataset.pluginId = pluginId;
				link.dataset.pluginCss = cssFileName;

				link.onload = () => {
					this.cssCache.add(cacheKey);
					console.log(`[Plugin] ${pluginId}: CSS loaded: ${cssFileName}`);
					resolve();
				};

				link.onerror = () => {
					console.error(`[Plugin] ${pluginId}: Failed to load CSS: ${cssFileName}`);
					// Don't reject - allow plugin to load without CSS
					resolve();
				};

				document.head.appendChild(link);
			});
		});

		await Promise.all(promises);
	}

	async load(manifest: PluginManifest): Promise<LoadedPlugin> {
		try {
			// If manifest is lightweight (from index) or missing config, load full manifest
			const isLightweight = !manifest.permissions && !manifest.bundle && manifest.manifest;
			const missingConfig = !manifest.config;

			if (isLightweight || missingConfig) {
				console.log(`[Plugin] ${manifest.id}: loading full manifest...`);
				const fullManifest = await this.getManifestById(manifest.id);
				if (!fullManifest) {
					throw new Error(`Failed to load full manifest for ${manifest.id}`);
				}
				manifest = fullManifest;
			}

			const isolation = (manifest as { security?: { isolation?: string } }).security?.isolation;
			if (isClass2IsolationEnabled() && isolation === 'class2') {
				return this.loadClass2(manifest);
			}

			// Basic version compatibility check
			const appVersion = (import.meta as any).env?.VITE_EXPERIENCE_VERSION || '1.0.0';
			if (manifest?.engine?.experience && !semverSatisfies(appVersion, manifest.engine.experience)) {
				console.warn(`[Plugin] ${manifest.id}: incompatible version (requires ${manifest.engine.experience})`);
				throw new Error(`Plugin ${manifest.id} requires Experience ${manifest.engine.experience}`);
			}
			
			// Load CSS files first (if defined in manifest.bundle.css)
			if (manifest.bundle?.css && manifest.bundle.css.length > 0) {
				console.log(`[Plugin] ${manifest.id}: loading ${manifest.bundle.css.length} CSS file(s)...`);
				await this.loadCSS(manifest.id, manifest.bundle.css);
			}
			
		// Load the ESM bundle — use path-based URL for plugins with public routes when on a public page
		const hasPublicRoutes = manifest.contributions?.routes?.some((r: any) => r.public === true);
		const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
		const onPublicPluginPage = hasPublicRoutes && (
			isPublicRoute(pathname) ||
			isManifestPublicPath(pathname, manifest.contributions?.routes)
		);
		const url = onPublicPluginPage
			? `${this.base}/api/plugins/bundle/${encodeURIComponent(manifest.id)}/${encodeURIComponent(manifest.version || '1.0.0')}`
			: `${this.base}/api/plugins/bundle?entry=${encodeURIComponent(manifest.id)}&id=${encodeURIComponent(manifest.id)}`;
		const mod: any = await this.importBundle(url);
		
		// 🔍 VERSION LOGGING: Always log to confirm correct version loaded (eliminates cache debugging!)
		const version = manifest.version || '?.?.?';
		const hash = manifest.bundle?.integrity?.substring(7, 19) || 'unknown';  // First 12 chars after "sha256:"
		const timestamp = new Date().toISOString().substring(0, 19).replace('T', ' ');
		console.log(`╔════════════════════════════════════════════════════════════════╗`);
		console.log(`║ 🔌 ${manifest.id.toUpperCase().padEnd(58)} ║`);
		console.log(`║ Version: ${version.padEnd(51)} ║`);
		console.log(`║ Hash: ${hash}...${' '.repeat(44)} ║`);
		console.log(`║ Loaded: ${timestamp}${' '.repeat(38)} ║`);
		console.log(`╚════════════════════════════════════════════════════════════════╝`);
		
		// Create plugin-specific SDK with authorization config
		const sdk = createPluginSdk(manifest.id, this.queryClient, manifest);
			
			// Extract routes: handle multiple export patterns:
			// 1. mod.default.routes (if default is an object containing routes)
			// 2. mod.routes (named export)
			// 3. mod.default (if default IS the routes object itself - common pattern)
			let routes = mod.default?.routes ?? mod.routes;
			if (!routes && mod.default && typeof mod.default === 'object') {
				// Check if mod.default IS the routes object (has component functions, no routes/widgets properties)
				const hasRoutesProperty = 'routes' in mod.default;
				const hasWidgetsProperty = 'widgets' in mod.default;
				const hasComponentFunctions = Object.values(mod.default).some((val: any) => typeof val === 'function');
				
				// If default has component functions but no routes/widgets properties, it's likely the routes object
				if (hasComponentFunctions && !hasRoutesProperty && !hasWidgetsProperty) {
					routes = mod.default;
					console.log(`[Plugin] ${manifest.id}: Using mod.default as routes object (${Object.keys(routes).length} components)`);
				}
			}
			
			// Log route extraction for debugging
			if (!routes) {
				console.warn(`[Plugin] ${manifest.id}: No routes found. Available exports:`, {
					hasDefault: !!mod.default,
					defaultType: typeof mod.default,
					defaultKeys: mod.default && typeof mod.default === 'object' ? Object.keys(mod.default) : null,
					hasRoutesExport: 'routes' in mod,
					allExports: Object.keys(mod)
				});
			} else {
				console.log(`[Plugin] ${manifest.id}: Extracted ${Object.keys(routes).length} route component(s):`, Object.keys(routes));
			}
			
			// Extract widgets: handle multiple export patterns:
			// 1. mod.default.widgets (if default is an object containing widgets)
			// 2. mod.widgets (named export)
			let widgets = mod.default?.widgets ?? mod.widgets;
			
			const plugin: LoadedPlugin = {
				id: manifest.id,
				manifest,
				routes: routes ?? {},
				widgets: widgets ?? {},
				sdk,  // Include SDK instance
			};
			
			this.loaded.set(manifest.id, plugin);
			
			// Log with PDP application scope for observability
			const pdpApp = manifest?.authorization?.pdp_application || 'experience';
			console.log(
				`[Plugin] ${manifest.id}: loaded successfully`,
				pdpApp !== 'experience' ? { pdp_application: pdpApp } : {}
			);
			
			return plugin;
		} catch (error: any) {
			// Detect bare-specifier errors and provide actionable guidance
			const msg = error?.message || String(error);
			if (msg.includes('resolve module specifier') || msg.includes('bare specifier')) {
				console.error(
					`[Plugin] ${manifest.id}: BUNDLE BUILD ERROR - contains bare module specifiers (e.g. "react", "react/jsx-runtime").\n` +
					`  FIX: Use the reactGlobalsShimPlugin in your build.mjs instead of external: ['react', 'react-dom'].\n` +
					`  See: plugins/_template/build.mjs and docs/COMPILE_AND_DEPLOY.md`
				);
			}
			console.error(`[Plugin] ${manifest.id}: failed to load`, error);
			throw error;
		}
	}

	/**
	 * Class 2 — load plugin in isolated iframe; shell brokers execute (no bearer in iframe).
	 */
	private async loadClass2(manifest: PluginManifest): Promise<LoadedPlugin> {
		const class2Origin =
			(import.meta as { env?: { VITE_PLUGIN_CLASS2_ORIGIN?: string } }).env
				?.VITE_PLUGIN_CLASS2_ORIGIN || window.location.origin;
		const sandboxUrl = class2SandboxFrameUrl(
			manifest.id,
			manifest.version || '1.0.0',
		);
		const sdk = createPluginSdk(manifest.id, this.queryClient, manifest);
		const broker = new Class2PluginBroker({
			pluginId: manifest.id,
			previewOrigin: class2Origin,
			shellOrigin: window.location.origin,
			onExecute: async (opRefId, params) => {
				const res = await fetch(`${this.base}/api/crud/execute`, {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ op_ref_id: opRefId, ...params }),
				});
				if (!res.ok) {
					throw new Error(`Class2 execute failed: ${res.status}`);
				}
				return res.json();
			},
		});
		const handler = (event: MessageEvent) => {
			const iframe = document.querySelector<HTMLIFrameElement>(
				`iframe[data-plugin-class2="${manifest.id}"]`,
			);
			if (iframe) broker.handleMessage(event, iframe);
		};
		window.addEventListener('message', handler);
		const plugin: LoadedPlugin = {
			id: manifest.id,
			manifest,
			routes: {},
			widgets: {},
			sdk,
			class2: { sandboxUrl, previewOrigin: class2Origin },
		};
		this.loaded.set(manifest.id, plugin);
		console.log(`[Plugin] ${manifest.id}: Class 2 isolation active`, { sandboxUrl });
		return plugin;
	}
	
	async loadAll(manifests: PluginManifest[]): Promise<LoadedPlugin[]> {
		// Use allSettled to prevent one failure from blocking others
		const results = await Promise.allSettled(manifests.map(m => this.load(m)));
		const successful: LoadedPlugin[] = [];
		const failed: string[] = [];
		
		results.forEach((result, index) => {
			if (result.status === 'fulfilled') {
				successful.push(result.value);
			} else {
				failed.push(manifests[index].id);
			}
		});
		
		if (failed.length > 0) {
			console.warn(`[Plugin] Failed to load ${failed.length} plugin(s): ${failed.join(', ')}`);
		}
		
		console.log(`[Plugin] Successfully loaded ${successful.length}/${manifests.length} plugins`);
		return successful;
	}

	getLoaded() { return Array.from(this.loaded.values()); }
	
	/**
	 * Check if a plugin is already loaded (for cache checking)
	 */
	isLoaded(pluginId: string): boolean {
		return this.loaded.has(pluginId);
	}
	
	/**
	 * Get an already-loaded plugin (returns null if not loaded)
	 */
	getLoadedPlugin(pluginId: string): LoadedPlugin | null {
		return this.loaded.get(pluginId) || null;
	}
}


